import type { Exercise, Lesson, Module } from '../types'

export const modules: Module[] = [
  {
    id: 'speaking-foundation',
    title: 'Find your speaking rhythm',
    eyebrow: 'Confidence · foundations',
    description: 'Build a calm starting routine for speaking before perfection gets in the way.',
    category: 'Confidence',
    objectives: ['Use a simple speaking warm-up', 'Introduce yourself with a clear structure', 'Recover smoothly when you need a moment'],
    levels: ['complete-beginner', 'basic', 'lower-intermediate', 'intermediate', 'not-sure'],
    goals: ['confidence', 'everyday', 'fluency'],
    challenges: ['hesitation', 'nervous', 'starting-point', 'limited-practice'],
    formats: ['short-lessons', 'guided-practice', 'scenarios'],
    estimatedMinutes: 25,
    practice: 'A 60-second self-introduction with a supportive restart drill.',
    lessonId: 'introduce-yourself',
    tags: ['core', 'speaking'],
  },
  {
    id: 'sentence-patterns',
    title: 'Make sentences feel natural',
    eyebrow: 'Grammar · fluency',
    description: 'Turn grammar knowledge into flexible sentence patterns you can reach for quickly.',
    category: 'Sentence building',
    objectives: ['Build clear subject–verb–detail sentences', 'Extend an idea with one useful detail', 'Use patterns without translating word by word'],
    levels: ['complete-beginner', 'basic', 'lower-intermediate', 'intermediate', 'upper-intermediate', 'not-sure'],
    goals: ['grammar', 'fluency', 'everyday', 'workplace'],
    challenges: ['translate', 'grammar-mistakes', 'application', 'hesitation'],
    formats: ['short-lessons', 'examples', 'quizzes'],
    estimatedMinutes: 30,
    practice: 'Rebuild everyday thoughts with a small set of reusable sentence frames.',
    lessonId: 'natural-sentences',
    tags: ['core', 'grammar'],
  },
  {
    id: 'rapid-response',
    title: 'Respond without freezing',
    eyebrow: 'Speaking · practice',
    description: 'Use short response moves so you can stay present in a conversation while you think.',
    category: 'Fluency',
    objectives: ['Buy thinking time naturally', 'Respond with short complete ideas', 'Ask a helpful follow-up question'],
    levels: ['basic', 'lower-intermediate', 'intermediate', 'upper-intermediate', 'advanced', 'not-sure'],
    goals: ['confidence', 'fluency', 'everyday', 'workplace'],
    challenges: ['hesitation', 'translate', 'nervous', 'limited-practice'],
    formats: ['guided-practice', 'scenarios', 'short-lessons'],
    estimatedMinutes: 20,
    practice: 'A rapid-response conversation loop with three everyday prompts.',
    lessonId: 'respond-without-hesitation',
    tags: ['core', 'speaking'],
  },
  {
    id: 'vocabulary-in-context',
    title: 'Put the right word in reach',
    eyebrow: 'Vocabulary · retrieval',
    description: 'Move vocabulary from recognition into useful, memorable situations.',
    category: 'Vocabulary',
    objectives: ['Notice meaning from context', 'Choose precise everyday words', 'Retrieve and reuse new vocabulary'],
    levels: ['basic', 'lower-intermediate', 'intermediate', 'upper-intermediate', 'advanced', 'not-sure'],
    goals: ['vocabulary', 'everyday', 'workplace', 'academic'],
    challenges: ['word-recall', 'limited-practice', 'application'],
    formats: ['examples', 'quizzes', 'guided-practice'],
    estimatedMinutes: 25,
    practice: 'A context clue challenge followed by a personal example sentence.',
    lessonId: 'vocabulary-context',
    tags: ['core', 'vocabulary'],
  },
  {
    id: 'pronunciation-clarity',
    title: 'Speak with more clarity',
    eyebrow: 'Pronunciation · intelligibility',
    description: 'Focus on being easy to understand, not on copying a particular accent.',
    category: 'Pronunciation',
    objectives: ['Stress key words in a sentence', 'Notice common sound contrasts', 'Use pauses to make meaning clearer'],
    levels: ['basic', 'lower-intermediate', 'intermediate', 'upper-intermediate', 'advanced', 'not-sure'],
    goals: ['pronunciation', 'confidence', 'presentations', 'workplace'],
    challenges: ['pronunciation', 'accents', 'nervous'],
    formats: ['short-lessons', 'examples', 'guided-practice'],
    estimatedMinutes: 30,
    practice: 'Read a short message twice, changing stress to make the key idea clear.',
    lessonId: 'pronunciation-clarity',
    tags: ['speaking'],
  },
  {
    id: 'listening-signals',
    title: 'Catch the meaning, not every word',
    eyebrow: 'Listening · comprehension',
    description: 'Build listening confidence with prediction, key words and clarification strategies.',
    category: 'Listening',
    objectives: ['Predict a conversation topic', 'Listen for the main idea', 'Ask for clarification respectfully'],
    levels: ['basic', 'lower-intermediate', 'intermediate', 'upper-intermediate', 'advanced', 'not-sure'],
    goals: ['listening', 'everyday', 'workplace', 'academic'],
    challenges: ['accents', 'application', 'nervous'],
    formats: ['examples', 'scenarios', 'guided-practice'],
    estimatedMinutes: 25,
    practice: 'A short workplace exchange with a main-idea and clarification check.',
    lessonId: 'listening-signals',
    tags: ['listening'],
  },
  {
    id: 'workplace-conversations',
    title: 'Contribute at work',
    eyebrow: 'Professional · communication',
    description: 'Practise useful language for meetings, updates, clarification and respectful disagreement.',
    category: 'Workplace communication',
    objectives: ['Give a concise project update', 'Clarify a request', 'Disagree with care and confidence'],
    levels: ['lower-intermediate', 'intermediate', 'upper-intermediate', 'advanced', 'not-sure'],
    goals: ['workplace', 'presentations', 'fluency'],
    challenges: ['hesitation', 'application', 'generic-courses', 'nervous'],
    formats: ['scenarios', 'guided-practice', 'examples'],
    estimatedMinutes: 35,
    practice: 'Choose a response in a meeting scenario, then adapt it to your own work.',
    lessonId: 'workplace-conversation',
    prerequisiteIds: ['sentence-patterns'],
    tags: ['professional'],
  },
  {
    id: 'interview-stories',
    title: 'Tell your story in interviews',
    eyebrow: 'Career · interview practice',
    description: 'Shape clear, specific answers that let your experience come through naturally.',
    category: 'Interview preparation',
    objectives: ['Structure a strong introduction', 'Answer with a clear example', 'Close an answer with the result or learning'],
    levels: ['lower-intermediate', 'intermediate', 'upper-intermediate', 'advanced', 'not-sure'],
    goals: ['interviews', 'workplace', 'confidence'],
    challenges: ['hesitation', 'nervous', 'application', 'translate'],
    formats: ['scenarios', 'guided-practice', 'examples'],
    estimatedMinutes: 35,
    practice: 'Build one STAR-style answer for a realistic interview prompt.',
    lessonId: 'interview-stories',
    prerequisiteIds: ['sentence-patterns'],
    tags: ['professional'],
  },
  {
    id: 'present-with-purpose',
    title: 'Present with purpose',
    eyebrow: 'Presentations · delivery',
    description: 'Organise an idea, signpost your message and land the point with confidence.',
    category: 'Presentations',
    objectives: ['Open with a clear purpose', 'Signpost three key points', 'Use a confident closing'],
    levels: ['intermediate', 'upper-intermediate', 'advanced', 'not-sure'],
    goals: ['presentations', 'workplace', 'academic'],
    challenges: ['nervous', 'pronunciation', 'application', 'generic-courses'],
    formats: ['examples', 'guided-practice', 'scenarios'],
    estimatedMinutes: 35,
    practice: 'Record or write a 90-second mini-presentation outline (no microphone needed).',
    lessonId: 'present-with-purpose',
    prerequisiteIds: ['sentence-patterns'],
    tags: ['professional'],
  },
  {
    id: 'weekly-recall',
    title: 'Make progress stick',
    eyebrow: 'Study system · consistency',
    description: 'Create a small weekly rhythm that turns practice into a habit you can keep.',
    category: 'Learning routine',
    objectives: ['Choose a realistic weekly target', 'Review with active recall', 'Reflect on one next step'],
    levels: ['complete-beginner', 'basic', 'lower-intermediate', 'intermediate', 'upper-intermediate', 'advanced', 'not-sure'],
    goals: ['fluency', 'vocabulary', 'confidence', 'other-goal'],
    challenges: ['routine', 'limited-practice', 'generic-courses'],
    formats: ['short-lessons', 'guided-practice', 'reading'],
    estimatedMinutes: 15,
    practice: 'A five-minute review ritual with one phrase, one response and one reflection.',
    lessonId: 'weekly-recall',
    tags: ['support'],
  },
]

export const lessons: Lesson[] = [
  {
    id: 'introduce-yourself', moduleId: 'speaking-foundation', label: 'Lesson 01', title: 'Introduce yourself with ease', duration: '8 min',
    objectives: ['Use a three-part introduction', 'Add one detail that invites conversation'],
    explanation: 'A confident introduction does not need to be long. Start with who you are, add what you do or care about, and finish with a small detail that gives the other person somewhere to go.',
    examples: ['“I’m Maya. I’m studying design, and I’m currently working on a community project.”', '“I’m Omar. I work in logistics, and I’m interested in making delivery systems more efficient.”'],
    exercises: [{ prompt: 'Which introduction gives the listener a natural opening for a follow-up question?', options: ['I am Ahmed. I am from Dubai.', 'I’m Ahmed. I work in product support, and I’m learning how to improve customer journeys.', 'My name is Ahmed. Thank you.'], answer: 'I’m Ahmed. I work in product support, and I’m learning how to improve customer journeys.', explanation: 'The second answer is clear and adds a detail that makes a follow-up possible. That is more useful than trying to share every detail at once.' }],
  },
  {
    id: 'natural-sentences', moduleId: 'sentence-patterns', label: 'Lesson 02', title: 'Build a natural everyday sentence', duration: '7 min',
    objectives: ['Use a flexible sentence frame', 'Add a reason with because'],
    explanation: 'When you need speed, use a reliable frame: “I’m + action/state + because + reason.” You can adapt it to work, study or daily life without translating every word.',
    examples: ['I’m practising English because I want to speak more naturally at work.', 'I’m taking the earlier train because I have a presentation this morning.'],
    exercises: [{ prompt: 'Choose the sentence that sounds complete and natural.', options: ['I practising English because work.', 'I’m practising English because I want to speak more naturally at work.', 'I am English practise want naturally.'], answer: 'I’m practising English because I want to speak more naturally at work.', explanation: 'The frame “I’m + -ing + because + full reason” keeps the sentence clear and gives you a structure to build on.' }],
  },
  {
    id: 'respond-without-hesitation', moduleId: 'rapid-response', label: 'Lesson 03', title: 'Respond while you think', duration: '6 min',
    objectives: ['Use a thinking phrase', 'Give a short first response'],
    explanation: 'You do not need to answer instantly. A small bridge such as “That’s a good question” or “Let me think about that for a moment” gives you time while keeping the conversation alive.',
    examples: ['“That’s a good question. I’d say the main reason is…”', '“Let me think about that. My first thought is…”'],
    exercises: [{ prompt: 'You need a moment to answer “What do you enjoy about your work?” What is the most helpful first response?', options: ['No.', 'That’s a good question. I’d say I enjoy solving practical problems.', 'I don’t know English.'], answer: 'That’s a good question. I’d say I enjoy solving practical problems.', explanation: 'The bridge phrase buys time and the short answer keeps you moving. Fluency is often about staying engaged, not speaking perfectly.' }],
  },
  {
    id: 'vocabulary-context', moduleId: 'vocabulary-in-context', label: 'Lesson 04', title: 'Learn words where they live', duration: '7 min',
    objectives: ['Use context to choose a word', 'Make a personal example'],
    explanation: 'A word becomes easier to remember when it is connected to a situation. Look at the words around a blank, then check whether the meaning fits the whole sentence.',
    examples: ['“Could you clarify the deadline?” = ask someone to make something clearer.', '“I’d like to follow up on our conversation.” = continue something discussed earlier.'],
    exercises: [{ prompt: 'Choose the word that fits: “Could you ___ what you mean by next week?”', options: ['clarify', 'bloom', 'attend'], answer: 'clarify', explanation: '“Clarify” means to make something clearer. The question is a polite way to ask for more detail.' }],
  },
  {
    id: 'workplace-conversation', moduleId: 'workplace-conversations', label: 'Lesson 05', title: 'Join a workplace conversation', duration: '9 min',
    objectives: ['Give a concise update', 'Ask for a useful next step'],
    explanation: 'A helpful update often has three parts: what is done, what is next, and where you need input. This keeps your message easy to follow in a meeting.',
    examples: ['“The first draft is ready. I’m checking the final details today, and I’d appreciate your feedback on the opening.”'],
    exercises: [{ prompt: 'A colleague asks for an update. Which answer is clearest?', options: ['It is okay, many things, maybe soon.', 'The first draft is ready. I’m checking the final details today, and I need feedback on the opening.', 'I work on it.'], answer: 'The first draft is ready. I’m checking the final details today, and I need feedback on the opening.', explanation: 'This answer gives status, next action and a specific request. Those three parts make workplace communication easier.' }],
  },
  {
    id: 'pronunciation-clarity', moduleId: 'pronunciation-clarity', label: 'Lesson 06', title: 'Make the key idea clear', duration: '7 min',
    objectives: ['Stress the important words', 'Use a pause to separate ideas'],
    explanation: 'Clear speech is not about losing your accent. Emphasise the words that carry the message and use a short pause between ideas.',
    examples: ['“I need the REPORT by FRIDAY.”', '“The plan is ready // but we need one more review.”'],
    exercises: [{ prompt: 'Which delivery makes the deadline easiest to understand?', options: ['I need the report by Friday.', 'I NEED the report by FRIDAY.', 'Every word has exactly the same stress.'], answer: 'I NEED the report by FRIDAY.', explanation: 'Stress on the action and the deadline helps the listener find the important information.' }],
  },
  {
    id: 'listening-signals', moduleId: 'listening-signals', label: 'Lesson 07', title: 'Listen for the main idea', duration: '7 min',
    objectives: ['Focus on meaning before details', 'Ask for clarification'],
    explanation: 'You do not need to catch every word. First listen for the topic, action and outcome. If one detail is missing, ask a focused clarification question.',
    examples: ['“So, just to check, should I send the revised version by Thursday?”'],
    exercises: [{ prompt: 'You missed one important detail in a meeting. What should you say?', options: ['Please say all the words again.', 'Could you clarify the deadline for the revised version?', 'I understand nothing.'], answer: 'Could you clarify the deadline for the revised version?', explanation: 'A focused question names the missing detail and helps the speaker answer efficiently.' }],
  },
  {
    id: 'interview-stories', moduleId: 'interview-stories', label: 'Lesson 08', title: 'Answer with a clear example', duration: '9 min',
    objectives: ['Give context for an answer', 'Connect action to result'],
    explanation: 'For experience questions, use a simple story arc: situation, action and result. You can be concise while still making your contribution specific.',
    examples: ['“Our response time was slow. I mapped the handoff process, removed one approval step, and the team began replying faster.”'],
    exercises: [{ prompt: 'Which answer shows what the speaker personally did?', options: ['The team had a problem and it was fixed.', 'We had a slow process, so I mapped the handoffs and removed one unnecessary approval step.', 'There was a process.'], answer: 'We had a slow process, so I mapped the handoffs and removed one unnecessary approval step.', explanation: 'The answer gives context and uses “I” to make the action specific. Add a result when you can.' }],
  },
  {
    id: 'present-with-purpose', moduleId: 'present-with-purpose', label: 'Lesson 09', title: 'Open a presentation clearly', duration: '8 min',
    objectives: ['State purpose and structure', 'Use signposting language'],
    explanation: 'A clear opening helps the audience follow you. Say why the topic matters, then preview the two or three points you will cover.',
    examples: ['“Today I’ll share two changes that can make our onboarding clearer. First, the welcome email; second, the first-week checklist.”'],
    exercises: [{ prompt: 'Which opening gives the audience a useful map?', options: ['Today I talk about onboarding.', 'Today I’ll share two changes that can make onboarding clearer: the welcome email and the first-week checklist.', 'Onboarding is a thing at work.'], answer: 'Today I’ll share two changes that can make onboarding clearer: the welcome email and the first-week checklist.', explanation: 'It gives purpose and previews the structure, so listeners know what to expect.' }],
  },
  {
    id: 'weekly-recall', moduleId: 'weekly-recall', label: 'Lesson 10', title: 'Build a tiny review ritual', duration: '5 min',
    objectives: ['Choose one phrase to reuse', 'Reflect on a real situation'],
    explanation: 'A small repeatable ritual is more useful than an ambitious schedule you cannot keep. Review one phrase, use it in a new sentence, and choose where you will try it next.',
    examples: ['Phrase: “Could you clarify…” → “Could you clarify the next step?” → Try it in tomorrow’s meeting.'],
    exercises: [{ prompt: 'Which review habit is easiest to repeat?', options: ['Study every English topic for three hours once a month.', 'Choose one useful phrase, make one personal example, and use it this week.', 'Wait until you feel perfectly ready.'], answer: 'Choose one useful phrase, make one personal example, and use it this week.', explanation: 'A small action connected to a real situation gives practice a clear purpose.' }],
  },
]
